/* The Studio chrome (`StudioParts.tsx`). Sizes follow the nearest container: the phone's by
 * default, the desktop's from 1024px of container width — `V3-Tel-*` against `V3-Desk-*`. */

.pill {
  display: flex;
  gap: var(--space-3);
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: var(--studio-pill-h);
  padding: var(--space-0) var(--space-6px) var(--space-0) var(--space-26px);
  font-family: var(--studio-font-body);
  color: var(--studio-pill-ink);
  text-align: left;
  background: var(--studio-pill);
  border: 0;
  border-radius: var(--r-pill);
  transition-property: background-color;
  transition-duration: var(--dur-base);
  transition-timing-function: var(--ease);
}

.pillControl {
  cursor: pointer;
}

.pillControl:hover {
  background: var(--studio-pill-hover);
}

.pillControl:focus-visible {
  outline: var(--border-focus) solid var(--studio-ink);
  outline-offset: var(--studio-focus-offset);
}

.pillControl[aria-disabled='true'] {
  cursor: not-allowed;
  opacity: var(--studio-o-refused);
}

.pillLabel {
  overflow: hidden;
  font-size: calc(var(--studio-fs-pill) * var(--studio-body-scale));
  font-weight: var(--fw-extrabold);
  letter-spacing: var(--studio-ls-tight);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pillArrow {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: var(--studio-pill-arrow);
  height: var(--studio-pill-arrow);
  color: var(--studio-on-accent);
  background: var(--studio-accent-fill);
  border-radius: var(--r-circle);
  transition-property: transform;
  transition-duration: var(--dur-slow);
  transition-timing-function: var(--ease-spring);
}

.pillControl:hover .pillArrow {
  transform: translateX(5px) rotate(-8deg);
}

.pillGlyph {
  width: var(--studio-icon-md);
  height: var(--studio-icon-md);
}

.back {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: var(--studio-pill-h);
  height: var(--studio-pill-h);
  padding: var(--space-0);
  color: var(--studio-ink);
  cursor: pointer;
  background: transparent;
  border: var(--border-hairline) solid var(--studio-line);
  border-radius: var(--r-circle);
  transition-property: border-color;
  transition-duration: var(--dur-base);
  transition-timing-function: var(--ease);
}

.back:hover {
  border-color: var(--studio-line-hover);
}

.back:focus-visible {
  outline: var(--border-focus) solid var(--studio-ink);
  outline-offset: var(--studio-focus-offset);
}

.backSmall {
  width: var(--studio-back-small);
  height: var(--studio-back-small);
}

.backGlyph {
  width: var(--studio-icon-md);
  height: var(--studio-icon-md);
}

.brand {
  display: flex;
  gap: var(--space-2);
  align-items: center;
  min-width: 0;
}

.logo {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: var(--studio-logo-size-sm);
  height: var(--studio-logo-size-sm);
  font-size: calc(var(--studio-fs-logo-sm) * var(--studio-body-scale));
  font-weight: var(--fw-extrabold);
  letter-spacing: var(--studio-ls-tight);
  color: var(--studio-logo-ink);
  background: var(--studio-logo-bg);
  border-radius: var(--studio-r-logo-sm);
}

.logoImage {
  flex: none;
  width: var(--studio-logo-size-sm);
  height: var(--studio-logo-size-sm);
  object-fit: contain;
}

.brandLarge .logo,
.brandLarge .logoImage {
  width: var(--studio-logo-size);
  height: var(--studio-logo-size);
}

.brandLarge .logo {
  font-size: calc(var(--studio-fs-logo) * var(--studio-body-scale));
  border-radius: var(--studio-r-logo);
}

.brandName {
  overflow: hidden;
  font-size: calc(var(--studio-fs-brand) * var(--studio-body-scale));
  font-weight: var(--fw-bold);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.avatar,
.avatarInitials {
  flex: none;
  width: var(--studio-avatar-header);
  height: var(--studio-avatar-header);
  border-radius: var(--r-circle);
}

.avatar {
  object-fit: cover;
}

.avatarInitials {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: calc(var(--studio-fs-avatar-initials) * var(--studio-body-scale));
  font-weight: var(--fw-extrabold);
  color: var(--studio-logo-ink);
  background: var(--studio-logo-bg);
}

.cap {
  display: inline-flex;
  gap: var(--space-1);
  align-items: center;
  justify-content: center;
  min-width: var(--studio-cap-h);
  height: var(--studio-cap-h);
  padding: var(--space-0) var(--space-7px);
  font-family: var(--studio-font-body);
  font-size: calc(var(--studio-fs-signature) * var(--studio-body-scale));
  font-weight: var(--fw-bold);
  color: var(--studio-ink-strong);
  border: var(--border-hairline) solid var(--studio-line-strong);
  border-bottom-width: var(--border-ring);
  border-radius: var(--studio-r-cap);
}

.poweredBy {
  display: inline-flex;
  gap: var(--space-6px);
  align-items: center;
  font-size: calc(var(--studio-fs-foot) * var(--studio-body-scale));

  /* Small words on black: 45% measured 4.3:1, under AA; 50% clears it. */
  color: var(--studio-ink-faint);
}

.mark {
  --mark-ink: currentcolor;

  width: var(--studio-mark-size);
  height: var(--studio-mark-size);
}

.grain {
  position: absolute;
  inset: 0;
  z-index: 5;
  width: 100%;
  height: 100%;
  pointer-events: none;
  mix-blend-mode: overlay;
  opacity: var(--studio-o-grain);
}

@container (min-width: 1024px) {
  .pill {
    height: var(--studio-pill-h-desk);
    padding: var(--space-0) var(--space-7px) var(--space-0) var(--space-7);
  }

  .pillLabel {
    font-size: calc(var(--studio-fs-pill-desk) * var(--studio-body-scale));
  }

  .pillArrow {
    width: var(--studio-pill-arrow-desk);
    height: var(--studio-pill-arrow-desk);
  }

  .back {
    width: var(--studio-pill-h-desk);
    height: var(--studio-pill-h-desk);
  }

  .backSmall {
    width: var(--studio-back-small-desk);
    height: var(--studio-back-small-desk);
  }

  .logo,
  .logoImage {
    width: var(--studio-logo-size-md);
    height: var(--studio-logo-size-md);
  }

  .logo {
    font-size: calc(var(--studio-fs-logo-md) * var(--studio-body-scale));
  }

  .brand {
    gap: var(--space-10px);
  }

  .brandName {
    font-size: calc(var(--studio-fs-card-name) * var(--studio-body-scale));
  }

  .brandLarge .logo,
  .brandLarge .logoImage {
    width: var(--studio-logo-size-desk);
    height: var(--studio-logo-size-desk);
  }

  .brandLarge .logo {
    font-size: calc(var(--studio-fs-logo-desk) * var(--studio-body-scale));
    border-radius: var(--studio-r-logo-desk);
  }

  .brandLarge .brandName {
    font-size: calc(var(--studio-fs-brand-desk) * var(--studio-body-scale));
  }

  .avatar,
  .avatarInitials {
    width: var(--studio-avatar-header-desk);
    height: var(--studio-avatar-header-desk);
  }

  .poweredBy {
    gap: var(--space-7px);
    font-size: calc(var(--studio-fs-signature) * var(--studio-body-scale));
  }

  .mark {
    width: var(--studio-mark-size-desk);
    height: var(--studio-mark-size-desk);
  }
}
